import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { referralAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Trophy, Medal, Star, TrendingUp, Users, ArrowLeft, Loader2 } from 'lucide-react';

export default function ReferralLeaderboardPage() {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');
  const [leaderboardData, setLeaderboardData] = useState(null);

  useEffect(() => {
    loadLeaderboard();
  }, [period]);

  const loadLeaderboard = async () => {
    setLoading(true);
    try {
      const res = await referralAPI.getLeaderboard(period);
      setLeaderboardData(res.data);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="h-6 w-6 text-yellow-500" />;
    if (rank === 2) return <Medal className="h-6 w-6 text-gray-400" />;
    if (rank === 3) return <Medal className="h-6 w-6 text-amber-600" />;
    return <span className="w-6 h-6 flex items-center justify-center text-muted-foreground font-bold">{rank}</span>;
  };

  const getRankBg = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200';
    if (rank === 2) return 'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200';
    if (rank === 3) return 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200';
    return 'bg-white';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Link>
            <Link to="/become-referral-agent">
              <Button variant="outline" size="sm">
                Become an Agent
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-12">
        <div className="container mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Trophy className="h-10 w-10" />
            <h1 className="text-3xl md:text-4xl font-bold">Top Referral Agents</h1>
          </div>
          <p className="text-blue-100 max-w-2xl mx-auto">
            Our amazing referral partners help travelers discover the best tours in Puerto Rico. 
            Join the program and start earning commissions today!
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Period Toggle */}
        <div className="flex justify-center mb-8">
          <Tabs value={period} onValueChange={setPeriod}>
            <TabsList>
              <TabsTrigger value="all" className="gap-2">
                <TrendingUp className="h-4 w-4" />
                All Time
              </TabsTrigger>
              <TabsTrigger value="monthly" className="gap-2">
                <Star className="h-4 w-4" />
                This Month
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : leaderboardData?.leaderboard?.length === 0 ? (
          <Card className="p-12 text-center">
            <Users className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-xl font-semibold mb-2">No agents on the leaderboard yet</h3>
            <p className="text-muted-foreground mb-6">
              Be the first to make your mark! Join our referral program and start earning.
            </p>
            <Link to="/become-referral-agent">
              <Button>Become a Referral Agent</Button>
            </Link>
          </Card>
        ) : (
          <>
            {/* Top 3 Showcase */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {leaderboardData?.leaderboard?.slice(0, 3).map((agent, index) => (
                <Card 
                  key={index} 
                  className={`p-6 text-center ${getRankBg(agent.rank)} border-2 ${index === 0 ? 'md:order-2 md:scale-105' : index === 1 ? 'md:order-1' : 'md:order-3'}`}
                >
                  <div className="flex justify-center mb-4">
                    {getRankIcon(agent.rank)}
                  </div>
                  <h3 className="text-xl font-bold mb-1">{agent.display_name}</h3>
                  {agent.badge && (
                    <Badge className="mb-3">{agent.badge}</Badge>
                  )}
                  <div className="text-3xl font-bold text-blue-600 mb-1">
                    {agent.total_conversions}
                  </div>
                  <p className="text-sm text-muted-foreground">Successful Bookings</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Member since {agent.joined}
                  </p>
                </Card>
              ))}
            </div>

            {/* Rest of the Leaderboard */}
            {leaderboardData?.leaderboard?.length > 3 && (
              <Card className="overflow-hidden">
                <div className="bg-muted px-6 py-3 border-b">
                  <h3 className="font-semibold">Full Rankings</h3>
                </div>
                <div className="divide-y">
                  {leaderboardData.leaderboard.slice(3).map((agent, index) => (
                    <div key={index} className="flex items-center justify-between px-6 py-4 hover:bg-muted/50">
                      <div className="flex items-center gap-4">
                        <span className="w-8 h-8 flex items-center justify-center bg-muted rounded-full font-bold text-muted-foreground">
                          {agent.rank}
                        </span>
                        <div>
                          <p className="font-medium">{agent.display_name}</p>
                          <p className="text-xs text-muted-foreground">Member since {agent.joined}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg">{agent.total_conversions}</p>
                        <p className="text-xs text-muted-foreground">bookings</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Stats Summary */}
            <div className="mt-8 text-center text-muted-foreground">
              <p>Showing top {leaderboardData?.leaderboard?.length || 0} of {leaderboardData?.total_agents || 0} active agents</p>
            </div>
          </>
        )}

        {/* CTA Section */}
        <Card className="mt-12 p-8 text-center bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-0">
          <h2 className="text-2xl font-bold mb-2">Want to Join the Leaderboard?</h2>
          <p className="text-blue-100 mb-6 max-w-xl mx-auto">
            Become a referral agent and earn commissions for every booking made through your unique links. 
            It's free to join!
          </p>
          <Link to="/become-referral-agent">
            <Button variant="secondary" size="lg">
              Apply Now
            </Button>
          </Link>
        </Card>
      </div>
    </div>
  );
}
